/**
 * Paths that render outside `UserRequiredContainer` and do not require authentication.
 * Kept in sync with `router.jsx`; see `@/common/utils/Paths/Paths.js` for canonical values.
 *
 * Exact match is used for static paths to prevent boundary-attack matches like
 * `/login-admin` masquerading as `/login`. Parameterized paths match on the
 * prefix with a trailing slash, so `/shared/v2fake/abc` will not match.
 */
const PUBLIC_EXACT_PATHS = new Set(['/signout', '/sorry', '/login', '/register', '/not-found'])
const PUBLIC_PREFIXES = ['/shared/v2/', '/store/', '/payments/']

/**
 * Returns true when the given pathname corresponds to a route that is allowed
 * to render without an authenticated session.
 *
 * Used by `KindeAuthBridge` to avoid clearing the provider cache and redirecting
 * to `/` on routes that are intentionally public (e.g., customer-facing quote
 * PDFs at `/shared/v2/...`). Without this, a Kinde-authenticated user opening
 * a public doc in a new tab would lose the session silently (QM-3308).
 *
 * @param {string} pathname - `window.location.pathname`-style path (no query, no hash)
 * @returns {boolean}
 */
export const isPublicPath = (pathname) => {
    if (typeof pathname !== 'string' || pathname.length === 0) return false
    if (PUBLIC_EXACT_PATHS.has(pathname)) return true
    return PUBLIC_PREFIXES.some((prefix) => pathname.startsWith(prefix))
}

/**
 * Protected paths that must survive an unauthenticated first hit, because the URL carries
 * state that cannot be rebuilt after a login round trip (e.g. an OAuth handshake's `state` and
 * PKCE challenge). Unlike PUBLIC_*, these routes still require a session — they are exempted
 * only from `KindeAuthBridge`'s purge-and-bounce, so the page can render and start its own
 * login. See `getPostLoginReturnTo` for sending the user back here once authenticated.
 */
const AUTH_RETURN_PATHS = new Set(['/integrations/zapier/authorize'])

/**
 * Returns true when the given pathname is allowed to render without an authenticated session —
 * either because it is genuinely public (`isPublicPath`), or because it is a protected route
 * that must be allowed to start its own login rather than being redirected to `/` first.
 *
 * Used by `KindeAuthBridge` in place of `isPublicPath` for its purge/bounce guards.
 * `getApiAccessToken` deliberately keeps `isPublicPath` — an `AUTH_RETURN_PATHS` route still
 * needs a real bearer token once authenticated, it is only exempt from the pre-emptive bounce.
 *
 * @param {string} pathname - `window.location.pathname`-style path (no query, no hash)
 * @returns {boolean}
 */
export const allowsUnauthenticatedEntry = (pathname) => isPublicPath(pathname) || AUTH_RETURN_PATHS.has(pathname)

/**
 * getPostLoginReturnTo - The path to send the user back to once login completes, if the given
 * pathname is one that needs it.
 *
 * @param {string} pathname - `window.location.pathname`-style path (no query, no hash)
 * @returns {string|undefined} The pathname, when it is in `AUTH_RETURN_PATHS`; otherwise
 *   undefined, so callers can omit the login option entirely rather than pass a falsy value.
 */
export const getPostLoginReturnTo = (pathname) => (AUTH_RETURN_PATHS.has(pathname) ? pathname : undefined)
